import type { Tab } from '../lib/route';
import { platformSite } from '../data/http';
import { tabPath } from '../lib/route';
import { initials, phoneView } from '../lib/format';
import { BookmarkIcon, CalendarIcon, CareIcon, ExitIcon, PersonIcon } from '../icons';

/* БОКОВОЕ МЕНЮ. Перенос макета, с одним отличием по существу: пункты — ССЫЛКИ, а
   не кнопки. Раздел у кабинета адресуемый (`/care`), значит средний щелчок и
   «открыть в новой вкладке» обязаны работать, как у любой ссылки. Переход при
   этом перехватывается — перезагружать бандл ради смены раздела незачем.

   СЧЁТЧИКИ И ТОЧКА ПОКАЗЫВАЮТСЯ ТОЛЬКО КОГДА ЕСТЬ ЧТО ПОКАЗАТЬ. В макете у
   визитов стоит «5», у врачей «3», у планов — точка «есть незакрытая задача».
   Нуль в кружке читается как «ноль чего-то важного», поэтому кружка при нуле
   нет вовсе; точка появляется, если хоть у одного плана есть незакрытые дела. */

export interface SidebarCounts {
    visits: number;
    doctors: number;
    /** Есть незакрытая задача хоть в одном плане — точка у «планов ухода». */
    careDot: boolean;
}

export function Sidebar({
    tab,
    onGo,
    fullName,
    phone,
    counts,
    onLogout,
}: {
    tab: Tab;
    onGo: (t: Tab) => void;
    fullName: string;
    phone: string;
    counts: SidebarCounts;
    onLogout: () => void;
}) {
    const site = platformSite();
    return (
        <aside className="side">
            {/* ЗНАК ВЕДЁТ НА ПУБЛИЧНЫЙ САЙТ — туда, где ищут врача по ссылке.
                Ссылкой он становится ТОЛЬКО когда адрес сайта известен
                (`VITE_PLATFORM_HOST`): запасного значения у него нет намеренно,
                и знак, ведущий в никуда, хуже знака, который просто знак.
                Внешний адрес, поэтому обычный переход, а не маршрут кабинета. */}
            {site !== null ? (
                <a className="wm wmgo" href={site}>
                    aventrae
                </a>
            ) : (
                <div className="wm">aventrae</div>
            )}

            {/* Плашка — вход в профиль. Кнопка, а не ссылка-`a` вокруг всего:
                внутри неё уже есть текст, и обёртка ссылкой дала бы подчёркивания
                и перетаскивание. Переход тот же, что у пункта меню «Профиль». */}
            <button type="button" className="me mego" onClick={() => onGo('profile')}>
                <span className="pearl">{initials(fullName)}</span>
                <span className="tx">
                    {/* ИМЕНИ МОЖЕТ НЕ БЫТЬ: аккаунт, вошедший по номеру, безымянный,
                        пока пациент не заполнит профиль. Тогда главной строкой
                        становится НОМЕР — он и есть то, чем пациент себя узнаёт, и
                        он же его логин. Фраза «Профиль не заполнен» на этом месте
                        не помещалась в чип и обрезалась посреди слова. */}
                    {fullName ? (
                        <>
                            <b>{fullName}</b>
                            <span>{phone ? phoneView(phone) : 'номер не указан'}</span>
                        </>
                    ) : (
                        <>
                            <b>{phone ? phoneView(phone) : 'Кабинет'}</b>
                            <span>имя не указано</span>
                        </>
                    )}
                </span>
            </button>

            <nav className="nav">
                <Item tab="visits" cur={tab} onGo={onGo} label="Визиты" count={counts.visits}>
                    <CalendarIcon />
                </Item>
                <Item tab="care" cur={tab} onGo={onGo} label="Планы ухода" dot={counts.careDot}>
                    <CareIcon />
                </Item>
                <Item tab="doctors" cur={tab} onGo={onGo} label="Избранные врачи" count={counts.doctors}>
                    <BookmarkIcon />
                </Item>
                <Item tab="profile" cur={tab} onGo={onGo} label="Профиль">
                    <PersonIcon />
                </Item>
            </nav>

            <div className="sfoot">
                <button type="button" className="sb" onClick={onLogout}>
                    <ExitIcon />
                    выйти
                </button>
            </div>
        </aside>
    );
}

function Item({
    tab,
    cur,
    onGo,
    label,
    count,
    dot,
    children,
}: {
    tab: Tab;
    cur: Tab;
    onGo: (t: Tab) => void;
    label: string;
    count?: number;
    dot?: boolean;
    children: React.ReactNode;
}) {
    return (
        <a
            className={tab === cur ? 'nb on' : 'nb'}
            href={tabPath(tab)}
            onClick={(e) => {
                /* Перехватываем ТОЛЬКО обычный щелчок. Средний, Cmd/Ctrl и Shift
                   отдаём браузеру: он откроет раздел в новой вкладке или окне, и
                   ломать это ради своей навигации нельзя. */
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                onGo(tab);
            }}
        >
            <span className="gl">{children}</span>
            <span className="t">{label}</span>
            {count !== undefined && count > 0 && <span className="cnt">{count}</span>}
            {dot === true && <span className="dot" title="есть незакрытая задача" />}
        </a>
    );
}
